import { useMemo, useRef, useState } from 'react';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Search, FileDown, ShoppingCart, DollarSign, Package,
  TrendingUp, ArrowDownToLine, ArrowUpFromLine, Maximize2,
} from 'lucide-react';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/Table';
import {
  useMaisComprados, useRaioXProduto,
  type RaioXFiltros, type ItemMaisComprado,
} from '@/hooks/useMercadoContratos';
import { DS_CATEGORICAL, DS_BRAND, chartSx, num } from './charts/chartTheme';
import { ChoroplethBR, NOME_UF } from './charts/ChoroplethBR';
import { Kpi, Painel, Toggle, Vazio } from './ui';
import { brl, brl2, nInt, pncpBusca } from './format';

/* ---------------------------------------------------------------- helpers */

const REGIOES: Record<string, string[]> = {
  Norte: ['AC', 'AP', 'AM', 'PA', 'RO', 'RR', 'TO'],
  Nordeste: ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE'],
  'Centro-Oeste': ['DF', 'GO', 'MT', 'MS'],
  Sudeste: ['ES', 'MG', 'RJ', 'SP'],
  Sul: ['PR', 'RS', 'SC'],
};

const RECENTS_KEY = 'alpendre:raiox:recentes';
function lerRecentes(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((x) => typeof x === 'string').slice(0, 6) : [];
  } catch { return []; }
}
function salvarRecente(termo: string): string[] {
  try {
    const atual = lerRecentes().filter((x) => x.toLowerCase() !== termo.toLowerCase());
    const prox = [termo, ...atual].slice(0, 6);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(prox));
    return prox;
  } catch { return lerRecentes(); }
}

/* ---------------------------------------------------------------- blocos */

/** Coluna de ranking da empty-state ("o que o governo mais compra"). */
function ColunaRanking({
  titulo, subtitulo, itens, metrica,
}: {
  titulo: string; subtitulo: string; itens: ItemMaisComprado[];
  metrica: (i: ItemMaisComprado) => string;
}) {
  return (
    <div className="rounded-lg border border-ds-border bg-ds-surface p-4">
      <h4 className="text-sm font-semibold text-ds-text-primary">{titulo}</h4>
      <p className="mb-3 text-[11px] text-ds-text-secondary">{subtitulo}</p>
      <ol className="space-y-2">
        {itens.map((it, idx) => (
          <li key={it.chave} className="flex items-start gap-2 text-sm">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ds-surface2 text-[11px] text-ds-text-secondary">
              {idx + 1}
            </span>
            <span className="min-w-0 flex-1 truncate text-ds-text-primary" title={it.descricao || it.chave}>
              {it.descricao || it.chave}
            </span>
            <span className="shrink-0 font-medium text-ds-brand">{metrica(it)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ---------------------------------------------------------------- principal */

export function RaioXMercado({ portal }: { portal?: boolean }) {
  // Busca
  const [termoInput, setTermoInput] = useState('');
  const [periodoMeses, setPeriodoMeses] = useState(12);
  // Filtros (locais → aplicados no "Pesquisar")
  const [uf, setUf] = useState('');
  const [regiao, setRegiao] = useState('');
  const [modalidade, setModalidade] = useState('');
  const [orgao, setOrgao] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [valorMin, setValorMin] = useState('');
  const [valorMax, setValorMax] = useState('');
  // Estado aplicado (dispara a query)
  const [q, setQ] = useState<RaioXFiltros | null>(null);
  const [recentes, setRecentes] = useState<string[]>(() => lerRecentes());
  // Toggles de visualização
  const [metricaMapa, setMetricaMapa] = useState<'compras' | 'valor' | 'unidades'>('compras');
  const [metricaEvo, setMetricaEvo] = useState<'compras' | 'valor'>('compras');
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const relatorioRef = useRef<HTMLDivElement>(null);

  function montarFiltros(termo: string): RaioXFiltros {
    const f: RaioXFiltros = { termo, periodoMeses: periodoMeses || undefined };
    if (regiao && REGIOES[regiao]) f.ufs = REGIOES[regiao].join(',');
    else if (uf.trim()) f.uf = uf.trim().toUpperCase();
    if (municipio.trim()) f.municipio = municipio.trim();
    if (modalidade.trim()) f.modalidade = modalidade.trim();
    if (orgao.trim()) f.orgao = orgao.trim();
    if (valorMin.trim()) f.valorMin = Number(valorMin);
    if (valorMax.trim()) f.valorMax = Number(valorMax);
    return f;
  }

  function pesquisar(termo: string) {
    const t = termo.trim();
    if (!t) return;
    setTermoInput(t);
    setQ(montarFiltros(t));
    setRecentes(salvarRecente(t));
  }

  const termoAtivo = q?.termo || '';
  const produto = useRaioXProduto(q ?? { termo: '' }, portal);
  const mais = useMaisComprados(
    { uf: uf.trim() || undefined, municipio: municipio.trim() || undefined, periodoMeses: periodoMeses || undefined },
    portal
  );
  const env = produto.data;

  // Rankings da empty-state (derivados client-side de um mesmo dataset).
  const maisDados = useMemo(() => mais.data ?? [], [mais.data]);
  const porEditais = useMemo(
    () => [...maisDados].sort((a, b) => b.editais - a.editais).slice(0, 8),
    [maisDados]
  );
  const porVolume = useMemo(
    () => [...maisDados].sort((a, b) => num(b.quantidade_total) - num(a.quantidade_total)).slice(0, 8),
    [maisDados]
  );
  const porValor = useMemo(
    () => [...maisDados].sort((a, b) => num(b.valor_total) - num(a.valor_total)).slice(0, 8),
    [maisDados]
  );

  // Ranking de estados conforme a métrica.
  const estados = useMemo(() => {
    const rows = env?.por_uf ?? [];
    const pick = (r: typeof rows[number]) =>
      metricaMapa === 'valor' ? num(r.valor_total)
        : metricaMapa === 'unidades' ? num(r.quantidade_total)
          : r.editais;
    return [...rows].sort((a, b) => pick(b) - pick(a));
  }, [env, metricaMapa]);

  // Mapa de calor: uf → valor na métrica ativa + máximo (p/ escala de opacidade).
  const { valoresUf, maxUf } = useMemo(() => {
    const m = new Map<string, number>();
    let max = 0;
    for (const r of env?.por_uf ?? []) {
      const v = metricaMapa === 'valor' ? num(r.valor_total)
        : metricaMapa === 'unidades' ? num(r.quantidade_total)
          : r.editais;
      if (r.uf) { m.set(r.uf, v); if (v > max) max = v; }
    }
    return { valoresUf: m, maxUf: max };
  }, [env, metricaMapa]);

  const fmtMetrica = (v: number) => (metricaMapa === 'valor' ? brl(v) : nInt(v));
  const labelMetrica = metricaMapa === 'valor' ? 'valor' : metricaMapa === 'unidades' ? 'unidades' : 'compras';

  function apresentar() {
    const el = relatorioRef.current;
    if (!el?.requestFullscreen) return;
    // Fundo/padding só enquanto em tela cheia (fora dela o layout normal manda).
    const aplicar = () => {
      const cheia = document.fullscreenElement === el;
      el.style.background = cheia ? 'rgb(var(--ds-bg))' : '';
      el.style.padding = cheia ? '2rem' : '';
      el.style.overflow = cheia ? 'auto' : '';
      if (!cheia) document.removeEventListener('fullscreenchange', aplicar);
    };
    document.addEventListener('fullscreenchange', aplicar);
    el.requestFullscreen().catch(() => document.removeEventListener('fullscreenchange', aplicar));
  }

  async function baixarPdf() {
    if (!relatorioRef.current) return;
    setGerandoPdf(true);
    try {
      const bg = getComputedStyle(document.body).backgroundColor || '#ffffff';
      const canvas = await html2canvas(relatorioRef.current, { backgroundColor: bg, scale: 2, useCORS: true });
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const ih = (canvas.height * pw) / canvas.width;
      let pos = 0;
      let restante = ih;
      pdf.addImage(img, 'PNG', 0, pos, pw, ih);
      restante -= ph;
      while (restante > 0) {
        pos -= ph;
        pdf.addPage();
        pdf.addImage(img, 'PNG', 0, pos, pw, ih);
        restante -= ph;
      }
      pdf.save(`raio-x-${(termoAtivo || 'mercado').replace(/\s+/g, '-')}.pdf`);
    } catch {
      // best-effort; falha de captura não deve quebrar a tela
    } finally {
      setGerandoPdf(false);
    }
  }

  const inputCls = 'rounded-md border border-ds-border bg-ds-surface px-2 py-1.5 text-sm text-ds-text-primary';

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------- busca */}
      <section className="rounded-xl border border-ds-border bg-ds-surface2 p-5">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ds-text-primary">
          <Search size={18} /> Raio-X de Mercado
        </h2>
        <p className="mb-3 text-sm text-ds-text-secondary">
          Descubra quanto o governo compra, onde compra e como compra — com dados reais do PNCP.
        </p>
        <form
          className="flex flex-wrap items-stretch gap-2"
          onSubmit={(e) => { e.preventDefault(); pesquisar(termoInput); }}
        >
          <div className="flex min-w-[220px] flex-[2] items-center gap-2 rounded-md border border-ds-border bg-ds-surface px-3">
            <Search size={16} className="text-ds-text-secondary" />
            <input
              value={termoInput}
              onChange={(e) => setTermoInput(e.target.value)}
              placeholder="Digite um produto ou serviço… (ex.: ar condicionado)"
              className="w-full bg-transparent py-2 text-sm text-ds-text-primary outline-none"
            />
          </div>
          <select value={periodoMeses} onChange={(e) => setPeriodoMeses(Number(e.target.value))} className={inputCls}>
            {[12, 24, 36].map((m) => <option key={m} value={m}>Últimos {m} meses</option>)}
            <option value={0}>Todo o período</option>
          </select>
          <button
            type="submit"
            className="rounded-md bg-ds-brand-solid px-4 py-2 text-sm font-semibold text-ds-on-brand disabled:opacity-60"
          >
            Pesquisar Mercado
          </button>
        </form>

        {recentes.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-ds-text-secondary">
            <span>Pesquisas recentes:</span>
            {recentes.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => pesquisar(r)}
                className="rounded-full border border-ds-border px-2.5 py-0.5 hover:bg-ds-surface2"
              >
                {r}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ------------------------------------------------- resultado OU empty-state */}
      {!termoAtivo ? (
        <section>
          <h2 className="mb-1 text-base font-semibold text-ds-text-primary">O que o governo mais compra</h2>
          <p className="mb-4 text-sm text-ds-text-secondary">
            Sem digitar nada — veja os itens mais comprados pelo setor público, com dados reais das licitações.
          </p>
          {mais.isLoading ? <Vazio texto="Carregando…" />
            : mais.isError ? <Vazio texto="Falha ao carregar." />
              : maisDados.length === 0 ? <Vazio texto="Sem itens indexados ainda. A base cresce com a coleta de itens do PNCP." />
                : (
                  <div className="grid gap-4 lg:grid-cols-3">
                    <ColunaRanking titulo="🏆 Mais solicitados" subtitulo="Itens que aparecem no maior número de licitações"
                      itens={porEditais} metrica={(i) => `${nInt(i.editais)} licit.`} />
                    <ColunaRanking titulo="📦 Maior volume" subtitulo="Itens com maior quantidade adquirida"
                      itens={porVolume} metrica={(i) => nInt(i.quantidade_total)} />
                    <ColunaRanking titulo="💰 Maior valor" subtitulo="Itens com maior valor somado nas licitações"
                      itens={porValor} metrica={(i) => brl(i.valor_total)} />
                  </div>
                )}
        </section>
      ) : produto.isLoading ? <Vazio texto="Carregando análise…" />
        : produto.isError ? <Vazio texto="Falha ao carregar a análise de mercado." />
          : !env?.quanto ? (
            <Vazio texto={`Nenhum item encontrado para "${termoAtivo}". A base de itens cresce com a coleta do PNCP.`} />
          ) : (
            <>
              {/* barra de ações + filtros */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-ds-text-secondary">
                  <span className="font-medium text-ds-text-primary">"{termoAtivo}"</span>
                  {' · '}{periodoMeses ? `últimos ${periodoMeses} meses` : 'todo o período'}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={apresentar}
                    className="flex items-center gap-1.5 rounded-md border border-ds-border px-3 py-1.5 text-sm text-ds-text-primary hover:bg-ds-surface2"
                    title="Abrir o relatório em tela cheia para apresentar"
                  >
                    <Maximize2 size={15} /> Modo Apresentação
                  </button>
                  <button
                    type="button"
                    onClick={baixarPdf}
                    disabled={gerandoPdf}
                    className="flex items-center gap-1.5 rounded-md border border-ds-border px-3 py-1.5 text-sm text-ds-text-primary hover:bg-ds-surface2 disabled:opacity-60"
                  >
                    <FileDown size={15} />{gerandoPdf ? 'Gerando…' : 'Baixar PDF'}
                  </button>
                </div>
              </div>

              <div className="grid gap-2 rounded-lg border border-ds-border bg-ds-surface p-3 sm:grid-cols-3 lg:grid-cols-6">
                <label className="flex flex-col gap-1 text-[11px] text-ds-text-secondary">Estado (UF)
                  <input value={uf} onChange={(e) => { setUf(e.target.value); setRegiao(''); }} placeholder="Todos" maxLength={2} className={inputCls} />
                </label>
                <label className="flex flex-col gap-1 text-[11px] text-ds-text-secondary">Região
                  <select value={regiao} onChange={(e) => { setRegiao(e.target.value); setUf(''); }} className={inputCls}>
                    <option value="">Todas</option>
                    {Object.keys(REGIOES).map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-[11px] text-ds-text-secondary">Modalidade
                  <input value={modalidade} onChange={(e) => setModalidade(e.target.value)} placeholder="Todas" className={inputCls} />
                </label>
                <label className="flex flex-col gap-1 text-[11px] text-ds-text-secondary">Órgão
                  <input value={orgao} onChange={(e) => setOrgao(e.target.value)} placeholder="Todos" className={inputCls} />
                </label>
                <label className="flex flex-col gap-1 text-[11px] text-ds-text-secondary">Município
                  <input value={municipio} onChange={(e) => setMunicipio(e.target.value)} placeholder="Todos" className={inputCls} />
                </label>
                <label className="flex flex-col gap-1 text-[11px] text-ds-text-secondary">Valor mín. (R$)
                  <input value={valorMin} onChange={(e) => setValorMin(e.target.value)} inputMode="numeric" placeholder="—" className={inputCls} />
                </label>
                <label className="flex flex-col gap-1 text-[11px] text-ds-text-secondary">Valor máx. (R$)
                  <div className="flex gap-1">
                    <input value={valorMax} onChange={(e) => setValorMax(e.target.value)} inputMode="numeric" placeholder="—" className={`${inputCls} w-full`} />
                    <button type="button" onClick={() => pesquisar(termoAtivo)} className="shrink-0 rounded-md bg-ds-brand-solid px-2 text-xs font-medium text-ds-on-brand">OK</button>
                  </div>
                </label>
              </div>

              {/* relatório capturável no PDF */}
              <div ref={relatorioRef} className="space-y-4">
                {/* KPIs */}
                <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  <Kpi icon={<ShoppingCart size={13} />} titulo="Compras" valor={nInt(env.quanto.editais)} detalhe="licitações encontradas" />
                  <Kpi icon={<DollarSign size={13} />} titulo="Valor movimentado" valor={brl(env.quanto.valor_total)} detalhe="valor estimado somado" />
                  <Kpi icon={<Package size={13} />} titulo="Qtd. de unidades" valor={nInt(env.quanto.quantidade_total)} detalhe="quantidade total" />
                  <Kpi icon={<TrendingUp size={13} />} titulo="Preço médio" valor={brl2(env.quanto.valor_unitario_medio)} detalhe={`${nInt(env.quanto.registros_com_valor)} itens com valor`} />
                  <Kpi icon={<ArrowDownToLine size={13} />} titulo="Menor valor" valor={brl2(env.quanto.valor_min)} detalhe="preço unitário mínimo" />
                  <Kpi icon={<ArrowUpFromLine size={13} />} titulo="Maior valor" valor={brl2(env.quanto.valor_max)} detalhe="preço unitário máximo" />
                </div>

                {/* onde (ranking estados) + como (donut) */}
                <div className="grid gap-4 lg:grid-cols-2">
                  <Painel
                    titulo="🗺️ Onde o governo mais compra"
                    acao={<Toggle value={metricaMapa} onChange={setMetricaMapa} options={[
                      { id: 'compras', label: 'Compras' }, { id: 'valor', label: 'Valor' }, { id: 'unidades', label: 'Unidades' },
                    ]} />}
                  >
                    {estados.length === 0 ? <Vazio texto="Sem dados por estado." /> : (
                      <div className="grid gap-3 sm:grid-cols-[1.3fr_1fr] sm:items-center">
                        <ChoroplethBR
                          ariaLabel="Mapa de calor das compras por estado"
                          fill={() => 'rgb(var(--ds-brand))'}
                          fillOpacity={(uf) => {
                            const v = valoresUf.get(uf);
                            if (!v || maxUf <= 0) return 0.08;
                            return 0.15 + 0.85 * Math.sqrt(v / maxUf); // sqrt p/ percepção
                          }}
                          title={(uf) => {
                            const v = valoresUf.get(uf);
                            return `${NOME_UF[uf]} (${uf})\n${v ? `${fmtMetrica(v)} em ${labelMetrica}` : 'sem compras no recorte'}`;
                          }}
                        />
                        <ol className="space-y-1.5 text-sm">
                          {estados.slice(0, 6).map((e, i) => {
                            const v = metricaMapa === 'valor' ? num(e.valor_total)
                              : metricaMapa === 'unidades' ? num(e.quantidade_total) : e.editais;
                            return (
                              <li key={e.uf || i} className="flex items-center justify-between gap-2">
                                <span className="inline-flex items-center gap-2 text-ds-text-primary">
                                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ds-surface2 text-[11px] text-ds-text-secondary">{i + 1}</span>
                                  {e.uf || '—'}
                                </span>
                                <span className="font-medium text-ds-brand">{fmtMetrica(v)}</span>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    )}
                  </Painel>

                  <Painel titulo="📊 Como o governo compra">
                    {(env.como ?? []).length === 0 ? <Vazio texto="Sem dados de modalidade." /> : (
                      <PieChart
                        colors={DS_CATEGORICAL}
                        height={240}
                        series={[{
                          innerRadius: 48,
                          paddingAngle: 2,
                          cornerRadius: 3,
                          data: env.como.slice(0, 6).map((c, i) => ({
                            id: i, value: num(c.valor_total), label: c.modalidade || '—',
                          })),
                          valueFormatter: (item) => brl(item.value),
                        }]}
                        slotProps={{ legend: { direction: 'column', position: { vertical: 'middle', horizontal: 'right' }, itemMarkWidth: 10, itemMarkHeight: 10, labelStyle: { fontSize: 11 } } }}
                        margin={{ right: 160 }}
                        sx={chartSx}
                      />
                    )}
                  </Painel>
                </div>

                {/* evolução (linha) */}
                <Painel
                  titulo="📈 Evolução das compras"
                  acao={<Toggle value={metricaEvo} onChange={setMetricaEvo} options={[
                    { id: 'compras', label: 'Compras' }, { id: 'valor', label: 'Valor' },
                  ]} />}
                >
                  {(env.evolucao ?? []).length === 0 ? <Vazio texto="Sem série temporal no período." /> : (
                    <LineChart
                      colors={[DS_BRAND]}
                      height={260}
                      xAxis={[{ scaleType: 'point', data: env.evolucao.map((e) => e.mes) }]}
                      series={[{
                        data: env.evolucao.map((e) => (metricaEvo === 'valor' ? num(e.valor_total) : e.editais)),
                        area: true,
                        showMark: env.evolucao.length <= 24,
                        valueFormatter: (v) => (metricaEvo === 'valor' ? brl(v ?? 0) : nInt(v ?? 0)),
                      }]}
                      margin={{ left: 56, right: 16, top: 12, bottom: 28 }}
                      sx={chartSx}
                    />
                  )}
                </Painel>

                {/* comportamento de preços (histograma) */}
                <Painel titulo="💹 Comportamento de preços">
                  <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div><div className="text-[11px] text-ds-text-secondary">Menor valor</div><div className="text-sm font-semibold text-ds-text-primary">{brl2(env.quanto.valor_min)}</div></div>
                    <div><div className="text-[11px] text-ds-text-secondary">Maior valor</div><div className="text-sm font-semibold text-ds-text-primary">{brl2(env.quanto.valor_max)}</div></div>
                    <div><div className="text-[11px] text-ds-text-secondary">Mediana</div><div className="text-sm font-semibold text-ds-text-primary">{brl2(env.quanto.valor_mediana)}</div></div>
                    <div><div className="text-[11px] text-ds-text-secondary">Registros</div><div className="text-sm font-semibold text-ds-text-primary">{nInt(env.quanto.registros_com_valor)}</div></div>
                  </div>
                  {(env.faixas ?? []).length === 0 ? <Vazio texto="Sem distribuição de preços." /> : (
                    <BarChart
                      colors={[DS_BRAND]}
                      height={240}
                      xAxis={[{ scaleType: 'band', data: env.faixas.map((f) => f.label) }]}
                      series={[{ data: env.faixas.map((f) => f.registros), valueFormatter: (v) => `${nInt(v ?? 0)} compras` }]}
                      margin={{ left: 44, right: 16, top: 12, bottom: 40 }}
                      sx={chartSx}
                    />
                  )}
                  <p className="mt-1 text-[11px] text-ds-text-secondary">Distribuição por faixa de valor por compra.</p>
                </Painel>

                {/* quem mais compra */}
                <Painel titulo="🏛️ Quem mais compra este produto">
                  {(env.quem ?? []).length === 0 ? <Vazio texto="Sem órgãos no recorte." /> : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Órgão</TableHead>
                          <TableHead>UF</TableHead>
                          <TableHead className="text-right">Compras</TableHead>
                          <TableHead className="text-right">Valor estimado</TableHead>
                          <TableHead className="text-right">PNCP</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {env.quem.map((o) => (
                          <TableRow key={`${o.orgao}-${o.uf}`}>
                            <TableCell className="max-w-[360px] truncate" title={o.orgao || ''}>{o.orgao || '—'}</TableCell>
                            <TableCell>{o.uf || '—'}</TableCell>
                            <TableCell className="text-right tabular-nums">{nInt(o.editais)}</TableCell>
                            <TableCell className="text-right tabular-nums">{brl(o.valor_total)}</TableCell>
                            <TableCell className="text-right">
                              <a href={pncpBusca(o.orgao || termoAtivo)} target="_blank" rel="noreferrer" className="text-ds-brand hover:underline">abrir</a>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Painel>
              </div>
            </>
          )}
    </div>
  );
}

export default RaioXMercado;
